/**
 * ProjectEditDialog — 投标人设置 Tab
 * 支持切换「我的企业」/「共享企业」来源
 */
import React, { useState } from 'react';
import { CompanyProfile } from '../types';

interface BidderSettingsTabProps {
  ownedCompanies: CompanyProfile[];
  sharedCompanies: CompanyProfile[];
  companyProfileIds: number[];
  setCompanyProfileIds: React.Dispatch<React.SetStateAction<number[]>>;
}

export default function BidderSettingsTab({
  ownedCompanies, sharedCompanies, companyProfileIds, setCompanyProfileIds,
}: BidderSettingsTabProps) {
  const [source, setSource] = useState<'owned' | 'shared'>('owned');
  const companies = source === 'owned' ? ownedCompanies : sharedCompanies;

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          关联企业 <span className="text-gray-400 font-normal">（可多选，支持联合体投标）</span>
        </label>

        {/* 来源切换 */}
        <div className="flex gap-1 mb-3">
          <button
            onClick={() => setSource('owned')}
            className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
              source === 'owned'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            我的企业
          </button>
          <button
            onClick={() => setSource('shared')}
            className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
              source === 'shared'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            共享企业
          </button>
        </div>

        {companies.length === 0 ? (
          <p className="text-xs text-gray-400 py-2">
            {source === 'owned'
              ? '暂无可用的企业信息，请在"我的设置 → 企业信息"中创建'
              : '暂无共享的企业信息'}
          </p>
        ) : (<>
          <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg divide-y divide-gray-100">
            {companies.map((c) => {
              const checked = companyProfileIds.includes(c.id);
              return (
                <label key={c.id} className="flex items-center px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors">
                  <input type="checkbox" checked={checked}
                    onChange={() => setCompanyProfileIds((prev) => checked ? prev.filter((id) => id !== c.id) : [...prev, c.id])}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                  <span className="ml-2 text-sm text-gray-700 flex-1">{c.name}</span>
                  {c.is_default && <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">默认</span>}
                  {source === 'shared' && (
                    <span className="text-xs text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded ml-1">共享</span>
                  )}
                </label>
              );
            })}
          </div>
          {companyProfileIds.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {companies.filter((c) => companyProfileIds.includes(c.id)).map((c) => (
                <span key={c.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-blue-50 text-blue-700">
                  {c.name}
                  <button type="button" onClick={() => setCompanyProfileIds((prev) => prev.filter((id) => id !== c.id))}
                    className="text-blue-400 hover:text-blue-600">×</button>
                </span>
              ))}
            </div>
          )}
        </>)}
      </div>
    </div>
  );
}
